import { IPoint2d } from '@/editor/math/IPoint2d';
import { modelManager } from '@/editor/manager/entityManager';
import { globalStores, TGlobalStores } from '@/editor/stores';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { EFaceSide, EToolType, EWallOperateType } from '../types';
import { createSeparateWallData } from '../data/separateWall/utils';
import { ESeparateWallType, SeparateWallBaseData, SeparateWallData } from '../data/separateWall/SeparateWall';
import { EFaceType } from '../entities/type';
import { EOperationType } from '../types/operation';
import { IWindowData } from '../data/model/model';
import { WallMoveCalculator } from '../utils/wall/wallMoveCalculator';
import { IOriginModifyWallInfo } from '../types';
import { isModifyWallsLegal } from '../stores/utils/legality/sepWall';
import { getPathFromEdges } from '../utils/edge';
import { getPointAndClosedRelation } from '../math/shape/path';
import { getShapeSeg } from '../math/shape/other';
import { segRelations } from '../math/shape/relation';
import { EPointAndClosedRelation, ERelationToSeg } from '../types/shape';
import { ArcSeg2d } from '../math/arcSeg2d';

type TSeparateWallArcParams =
  | {
      center: IPoint2d;
      clockwise: boolean;
    }
  | {
      center?: undefined;
      clockwise?: undefined;
    };

function hasSegRelation(relation: ERelationToSeg | ERelationToSeg[], target: ERelationToSeg) {
  return Array.isArray(relation) ? relation.includes(target) : relation === target;
}

export class SeparateWallOperation {
  private _wallStore: TGlobalStores['wallStore'];
  private _separateWallStore: TGlobalStores['separateWallStore'];
  private _roomStore: TGlobalStores['roomStore'];
  private _faceStore: TGlobalStores['faceStore'];
  private _gs: TGlobalStores;

  private _wallPan: WallMoveCalculator;

  constructor(gs: TGlobalStores) {
    this._gs = gs;
    this._wallStore = gs.wallStore;
    this._separateWallStore = gs.separateWallStore;
    this._roomStore = gs.roomStore;
    this._faceStore = gs.faceStore;

    this._wallPan = new WallMoveCalculator(this._separateWallStore);
  }

  get globalWallDatas() {
    return this._wallStore.getWallDatas();
  }

  get globalSeparateWallDatas() {
    return this._separateWallStore.separateWallDatas;
  }

  get globalRoomDatas() {
    return this._roomStore.getRoomDatas();
  }

  private $history_addSeparateWall(separateWallDatas: SeparateWallData[]) {
    this._gs.history.startOperation();

    separateWallDatas.forEach((separateWallData) => {
      this._gs.separateWallStore.addSepData(separateWallData, false);
    });

    // 按房间更新区域数据
    const roomIds = separateWallDatas.map((swd) => swd.relateRoomId);
    for (const roomId of roomIds) {
      this._gs.refreshByRoomId(roomId);
    }

    // 将操作记录压栈
    this._gs.history.completeOperation(EOperationType.SeparateWallAdd);
  }

  /**
   * 删除隔墙的数据操作本体，不含 history 事务
   *
   * 供多选删除这类复合操作复用，让它们能把隔墙和其他构件合进同一条撤销记录
   */
  public applyRemoveSeparateWalls(separateWallDatas: SeparateWallData[]) {
    const relativeInsertElements: IWindowData[] = [];
    separateWallDatas.forEach((separateWallData) => {
      relativeInsertElements.push(...this._gs.modelStore.getWindowsByWallId(separateWallData.id));
    });
    relativeInsertElements.forEach((windowData) => {
      this._gs.modelStore.removeWindowData(windowData.id);
    });

    separateWallDatas.forEach((separateWallData) => {
      this._gs.separateWallStore.removeSepData(separateWallData.id, false);
    });

    const roomIds = separateWallDatas.map((swd) => swd.relateRoomId);
    // this._gs.separateWallStore.updateRoomSeparateWallsShape(Array.from(new Set(roomIds)));
    for (const roomId of roomIds) {
      this._gs.regionStore.refreshRegionDatasByRoomId(roomId);
    }
    this._faceStore.refreshFaceDatas();
  }

  private $history_delSeparateWall(separateWallDatas: SeparateWallData[]) {
    this._gs.history.startOperation();

    this.applyRemoveSeparateWalls(separateWallDatas);

    // 将操作记录压栈
    this._gs.history.completeOperation(EOperationType.SeparateWallDel);
  }

  private $history_modifySeparateWalls(modifySpdDatas: { id: string; data: Partial<SeparateWallBaseData> }[]) {
    this._gs.history.startOperation();

    let roomId = '';
    const typeChangeSepDatas: Record<string, ESeparateWallType> = {};
    modifySpdDatas.forEach((msd) => {
      const currentSeparateWallData = this._gs.separateWallStore.getSepData(msd.id, false);
      if (!currentSeparateWallData) return;

      roomId = currentSeparateWallData.relateRoomId;

      // TODO: 待优化，可以将删除逻辑添加到updateDoorWindowData中
      if (msd.data?.type === ESeparateWallType.VirtualWall) {
        this._gs.modelStore.getWindowsByWallId(msd.id).forEach((windowData) => {
          this._gs.modelStore.removeWindowData(windowData.id);
        });
      }

      // 隔墙类型切换时，墙顶/底面进行材质替换
      const nextType = msd.data.type;
      const isTypeChange = nextType !== undefined && currentSeparateWallData.type !== nextType;
      if (isTypeChange) typeChangeSepDatas[currentSeparateWallData.id] = nextType;
    });
    this._gs.separateWallStore.updateSeparateWallDatas(modifySpdDatas);

    // 等隔墙数据更新完成后，再更新门窗数据
    modifySpdDatas.forEach((msd) => {
      this._gs.updateDoorWindowData(msd.id);
    });

    // 最后计算更新区域轮廓
    this._gs.refreshByRoomId(roomId);

    // 开始隔墙顶/底面进行材质替换
    if (Object.keys(typeChangeSepDatas).length > 0) {
      this._faceStore.getSepWallFaceDatas().forEach((faceData) => {
        if (
          !faceData.relativeInfo.sepId ||
          !typeChangeSepDatas[faceData.relativeInfo.sepId] ||
          (faceData.relativeInfo.sepSide !== EFaceSide.TopSide &&
            faceData.relativeInfo.sepSide !== EFaceSide.BottomSide)
        )
          return;

        this._faceStore.updateSepWallMaterial(
          faceData.id,
          globalStores.getDefaultMaterial({
            type: EFaceType.SepWallFace,
            sepWallData: { type: typeChangeSepDatas[faceData.relativeInfo.sepId] }
          })
        );
      });
    }

    // 将操作记录压栈
    this._gs.history.completeOperation(EOperationType.SeparateWallModify);
  }

  addSeparateWall(startPoint: IPoint2d, endPoint: IPoint2d, roomId: string, arcParams: TSeparateWallArcParams = {}) {
    if (!roomId) return false;

    const isLegal = this.isLegal(startPoint, endPoint, roomId, arcParams).isLegal;
    if (!isLegal) return false;

    const start = Point2dUtils.clone(startPoint);
    const end = Point2dUtils.clone(endPoint);
    const center = arcParams.center ? Point2dUtils.clone(arcParams.center) : undefined;

    const { layerHeight, offGround } = globalStores.paramStore;
    const separateWallData = createSeparateWallData({
      roomId,
      start,
      end,
      center,
      clockwise: arcParams.clockwise,
      thickness:
        globalStores.operateInfo.toolType === EToolType.virtualSeparateWall
          ? 0
          : globalStores.paramStore.separateWallThickness,
      height: layerHeight,
      offGround,
      type:
        globalStores.operateInfo.toolType === EToolType.virtualSeparateWall
          ? ESeparateWallType.VirtualWall
          : globalStores.paramStore.separateWallType
    });

    this.$history_addSeparateWall([separateWallData]);

    // TODO: 待优化，只更新房间相关
    modelManager.updateEntity();

    // 添加完一个墙体时，绘制状态关闭，要后续移动了鼠标后才会开启
    // this._drawing = false;

    return true;
  }

  removeSeparateWall(separateId: string) {
    const separateWallData = globalStores.separateWallStore.getSepData(separateId, false);
    if (!separateWallData) return;

    this.$history_delSeparateWall([separateWallData]);

    modelManager.updateEntity();
  }

  modifySeparateWalls(modifySpdDatas: { id: string; data: Partial<SeparateWallBaseData> }[]) {
    this.$history_modifySeparateWalls(modifySpdDatas);

    modelManager.updateEntity(); // TODO: 待优化，只更新房间相关
    modelManager.selectedRerender();
  }

  // 修改隔墙类型
  modifySeparateWallType(separateId: string, type: ESeparateWallType) {
    const currentSeparateWallData = this._separateWallStore.getSepData(separateId, false);
    if (!currentSeparateWallData || currentSeparateWallData.type === type) return;

    // 处理玻璃墙/实体墙切换，进行下材质替换，其他切换类型切换时，face会重新创建，不需要处理
    if (
      (currentSeparateWallData.type === ESeparateWallType.GlassWall && type === ESeparateWallType.SolidTabletWall) ||
      (currentSeparateWallData.type === ESeparateWallType.SolidTabletWall && type === ESeparateWallType.GlassWall)
    ) {
      const sepFaces = this._faceStore.getSepWallFaceDatas().filter((face) => face.relativeInfo.sepId === separateId);

      sepFaces.forEach((face) => {
        this._faceStore.updateSepWallMaterial(
          face.id,
          globalStores.getDefaultMaterial({
            type: EFaceType.SepWallFace,
            sepWallData: { type }
          })
        );
      });
    }

    this.modifySeparateWalls([{ id: separateId, data: { type } }]);
  }

  isLegal(
    startPoint: IPoint2d,
    endPoint: IPoint2d,
    roomId: string,
    arcParams: TSeparateWallArcParams = {}
  ): { isLegal: boolean; reason: string } {
    const roomData = this._roomStore.getRoomData(roomId);
    if (!roomData) {
      return {
        isLegal: false,
        reason: '房间不存在'
      };
    }

    // 内轮廓
    const roomPath = getPathFromEdges(roomData.pathInfos);
    const newSeg = getShapeSeg({ start: startPoint, end: endPoint, ...arcParams });
    const checkPoints = newSeg instanceof ArcSeg2d ? newSeg.getPoints() : [startPoint, endPoint];
    if (newSeg instanceof ArcSeg2d && checkPoints.length) {
      // 通过getPoints的取样点是通过弧度计算得到的，所以起始点可能会有误差，这里用绘制的起始点替代
      checkPoints[0] = startPoint;
      checkPoints[checkPoints.length - 1] = endPoint;
    }

    if (checkPoints.some((point) => getPointAndClosedRelation(point, roomPath) === EPointAndClosedRelation.Outside)) {
      // 端点不在房间内或边上
      return { isLegal: false, reason: '隔墙不在房间内或边上' };
    }

    const holePaths = roomData.holeInfos.map((hole) => getPathFromEdges(hole));
    if (
      holePaths.some((path) =>
        checkPoints.some((point) => getPointAndClosedRelation(point, path) === EPointAndClosedRelation.Inside)
      )
    ) {
      // 端点在房间洞内
      return { isLegal: false, reason: '隔墙在房间洞内' };
    }

    // ================= 相交检测 =================
    const sepWallDatas = this._separateWallStore.getSepDatas({ roomIds: [roomId] });
    const sepWallSegs = sepWallDatas.map((swd) => getShapeSeg(swd));
    for (const seg of sepWallSegs) {
      const relation = segRelations(newSeg, seg);

      if (
        hasSegRelation(relation, ERelationToSeg.Overlap) ||
        hasSegRelation(relation, ERelationToSeg.Same) ||
        hasSegRelation(relation, ERelationToSeg.Opposite)
      ) {
        return { isLegal: false, reason: '隔墙与其他墙体重合' };
      }

      if (hasSegRelation(relation, ERelationToSeg.IntersectInternal)) {
        return { isLegal: false, reason: '隔墙与其他墙体相交' };
      }
    }

    return { isLegal: true, reason: '' };
  }

  pan(wallId: string, offset: number, direction?: IPoint2d) {
    const wallData = this._separateWallStore.getSepData(wallId, false);
    if (!wallData) return false;

    this._wallPan.setMainWall(wallId);
    const result = this._wallPan.compute({ type: EWallOperateType.Pan, offset, direction });
    if (!result) return false;

    const isSuccess = this._modifyExecute(result.modifyWallInfos);
    return isSuccess;
  }

  moveSeparateWallPoint(
    wallId: string,
    operateData: {
      type: EWallOperateType.StartPoint | EWallOperateType.EndPoint | EWallOperateType.ArcMidPoint;
      point: IPoint2d;
    }
  ) {
    const wallData = this._separateWallStore.getSepData(wallId, false);
    if (!wallData) return false;

    this._wallPan.setMainWall(wallId);
    const { modifyWallInfos } = this._wallPan.compute(operateData);

    return this._modifyExecute(modifyWallInfos);
  }

  // 暂不支持有重叠的墙体数据入参
  private async _modifyExecute(modifyWallInfos?: IOriginModifyWallInfo[]) {
    if (!modifyWallInfos) return false;

    const { isLegal } = isModifyWallsLegal({ modifyWallInfos });
    if (!isLegal) return false;

    this.$history_modifySeparateWalls(modifyWallInfos.map((info) => ({ id: info.wallId, data: info.updateBaseData })));

    modelManager.updateEntity();

    return true;
  }
}
